import {
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import ReportTabsChartType from "components/report/ReportTabsChartType/ReportTabsChartType";
import { ChartType } from "enums/report";
import {AxisMapped, FieldType, ReportSheet, YAxisID, YValue} from "interfaces/report";
import { isFunction } from "lodash";
import {Chart} from "react-chartjs-2";

interface Props {
  chartType: ChartType | undefined;
  isOwner: boolean;
  sheet: ReportSheet<AxisMapped>;
  handleChangeAxisLabels?: (key: string, axis: "xAxis" | "yAxis") => void;
  handleChangeChartType?: Function;
  handleChangeYAxisID?: (keys: string[], id: YAxisID) => void;
  handleChangeXAxis?: (key: string) => void;
  handleChangeXLabel?: (key: string) => void;
  onChangeAxisUnit?: (axisId: YAxisID, unit: string) => void;
  handleChangeBarLineField?: (keys: string[], fieldType: FieldType) => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function ChartSelect({
  chartType,
  isOwner,
  sheet,
  handleChangeAxisLabels,
  handleChangeChartType,
  handleChangeYAxisID,
  handleChangeXAxis,
  handleChangeBarLineField,
  handleChangeXLabel, onChangeAxisUnit,
}: Props) {
  const yAxis = sheet.yAxis.filter((item) => !item.hide);

  const leftUnit = sheet.yAxis.filter((item) => !item.hide)
      .filter((item) => item.yAxisID === YAxisID.Y)[0]?.unit || '';

  const rightUnit = sheet.yAxis.filter((item) => !item.hide)
      .filter((item) => item.yAxisID === YAxisID.Y1)[0]?.unit || '';

  const yIds = sheet.yAxis
    .filter((item) => !item.hide)
    .filter((item) => item.yAxisID === YAxisID.Y)
    .map((item) => item.value);

  const y1Ids = sheet.yAxis
    .filter((item) => !item.hide)
    .filter((item) => item.yAxisID === YAxisID.Y1)
    .map((item) => item.value);

  const barIds = sheet.yAxis
    .filter((item) => !item.hide)
    .filter((item) => item.fieldType === FieldType.BAR)
    .map((item) => item.value);

  const lineIds = sheet.yAxis
    .filter((item) => !item.hide)
    .filter((item) => item.fieldType === FieldType.LINE)
    .map((item) => item.value);
  return (
      <>
        <Stack spacing={2} flexWrap="wrap" direction="row">
          <ReportTabsChartType
              isOwner={isOwner}
              chartType={chartType}
              handleChangeChartType={handleChangeChartType}
          />
        </Stack>
        <Stack marginTop={2} spacing={2} flexWrap="wrap" direction="row">
          {chartType && (
              <>
                {[ChartType.DOUGHNUT, ChartType.PIE].includes(chartType) ? (
                    <Stack width={200} spacing={1}>
                      <InputLabel htmlFor="chart-type">Field Type</InputLabel>
                      <Select
                          variant="outlined"
                          value={sheet.yAxis.find((item) => item.active)?.value || ""}
                          onChange={(event) =>
                              isFunction(handleChangeAxisLabels) &&
                              handleChangeAxisLabels(event.target.value, "yAxis")
                          }
                      >
                        {sheet.yAxis
                            .filter((item) => !item.hide)
                            .map((item, key) => {
                              return (
                                  <MenuItem key={key} value={item.value}>
                                    {item.value}
                                  </MenuItem>
                              );
                            })}
                      </Select>
                    </Stack>
                ) : (
                    <Stack width={200} spacing={1}>
                      <InputLabel htmlFor="chart-type">X Axis</InputLabel>
                      <Select
                          variant="outlined"
                          value={sheet.yAxis.find((item) => item.isXAxis)?.value || ""}
                          onChange={(event) =>
                              isFunction(handleChangeXAxis) &&
                              handleChangeXAxis(event.target.value)
                          }
                      >
                        {sheet.yAxis.map((item, key) => {
                          return (
                              <MenuItem key={key} value={item.value}>
                                {item.value}
                              </MenuItem>
                          );
                        })}
                      </Select>
                    </Stack>
                )}
                {[
                  ChartType.MULTI_AXIS,
                  ChartType.TREND_LINE,
                  ChartType.LINE,
                  ChartType.BAR,
                  ChartType.STACKED_BAR,
                  ChartType.STACKED_BAR_GROUP,
                ].includes(chartType) && (
                    <>
                      <Stack width={300} spacing={1}>
                        <InputLabel htmlFor="left-y-axis">Left Y Axis</InputLabel>
                        <Select
                            labelId="left-y-axis"
                            id="left-y-axis"
                            multiple
                            value={yIds}
                            onChange={(e: any) =>
                                isFunction(handleChangeYAxisID) &&
                                handleChangeYAxisID(e.target.value, YAxisID.Y)
                            }
                            input={<OutlinedInput label="left-y-axis" />}
                            MenuProps={MenuProps}
                        >
                          {yAxis.map((item, idx) => (
                              <MenuItem key={idx} value={item.value}>
                                {item.value}
                              </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                      <Stack width={100} spacing={1}>
                        <InputLabel htmlFor="left-y-axis">Left Unit</InputLabel>
                        <Select
                            labelId="left-y-axis"
                            id="left-y-axis"
                            value={[leftUnit]}
                            onChange={(e: any) =>
                                isFunction(onChangeAxisUnit) &&
                                onChangeAxisUnit(YAxisID.Y, e.target.value)
                            }
                            input={<OutlinedInput label="left-y-axis" />}
                            MenuProps={MenuProps}
                        >
                          <MenuItem value={""}>None</MenuItem>
                          <MenuItem value={YValue.PERCENT}>%</MenuItem>
                          <MenuItem value={YValue.PRICE}>$</MenuItem>
                        </Select>
                      </Stack>

                      {
                          [ChartType.MULTI_AXIS].includes(chartType) && <>
                            <Stack width={300} spacing={1}>
                              <InputLabel htmlFor="right-y-axis">Right Y Axis</InputLabel>
                              <Select
                                  labelId="right-y-axis"
                                  id="right-y-axis"
                                  multiple
                                  value={y1Ids}
                                  onChange={(e: any) =>
                                      isFunction(handleChangeYAxisID) &&
                                      handleChangeYAxisID(e.target.value, YAxisID.Y1)
                                  }
                                  input={<OutlinedInput label="right-y-axis" />}
                                  MenuProps={MenuProps}
                              >
                                {yAxis.map((item, idx) => (
                                    <MenuItem key={idx} value={item.value}>
                                      {item.value}
                                    </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            <Stack width={100} spacing={1}>
                              <InputLabel htmlFor="left-y-axis">Right Unit</InputLabel>
                              <Select
                                  labelId="left-y-axis"
                                  id="left-y-axis"
                                  value={[rightUnit]}
                                  onChange={(e: any) =>
                                      isFunction(onChangeAxisUnit) &&
                                      onChangeAxisUnit(YAxisID.Y1, e.target.value)
                                  }
                                  input={<OutlinedInput label="left-y-axis" />}
                                  MenuProps={MenuProps}
                              >
                                <MenuItem value={""}>None</MenuItem>
                                <MenuItem value={YValue.PERCENT}>%</MenuItem>
                                <MenuItem value={YValue.PRICE}>$</MenuItem>
                              </Select>
                            </Stack>
                          </>
                      }
                    </>
                )}
                {[ChartType.STACKED_BAR_GROUP].includes(chartType) && (
                    <Stack width={200} spacing={1}>
                      <InputLabel htmlFor="chart-type">Field Group</InputLabel>
                      <Select
                          variant="outlined"
                          value={
                              sheet.yAxis.find((item) => item.isFieldGroup)?.value || ""
                          }
                          onChange={(event) =>
                              isFunction(handleChangeXLabel) &&
                              handleChangeXLabel(event.target.value)
                          }
                      >
                        {sheet.yAxis.map((item, key) => {
                          return (
                              <MenuItem key={key} value={item.value}>
                                {item.value}
                              </MenuItem>
                          );
                        })}
                      </Select>
                    </Stack>
                )}
                {[ChartType.STACKED_BAR_LINE].includes(chartType) && (
                    <>
                      <Stack width={300} spacing={1}>
                        <InputLabel htmlFor="bar-field">Bar Field</InputLabel>
                        <Select
                            labelId="bar-field"
                            id="bar-field"
                            multiple
                            value={barIds}
                            onChange={(e: any) =>
                                isFunction(handleChangeBarLineField) &&
                                handleChangeBarLineField(e.target.value, FieldType.BAR)
                            }
                            input={<OutlinedInput label="bar-field" />}
                            MenuProps={MenuProps}
                        >
                          {yAxis.map((item, idx) => (
                              <MenuItem key={idx} value={item.value}>
                                {item.value}
                              </MenuItem>
                          ))}
                        </Select>
                      </Stack>

                      <Stack width={300} spacing={1}>
                        <InputLabel htmlFor="line-field">Line Field</InputLabel>
                        <Select
                            labelId="line-field"
                            id="line-field"
                            multiple
                            value={lineIds}
                            onChange={(e: any) =>
                                isFunction(handleChangeBarLineField) &&
                                handleChangeBarLineField(e.target.value, FieldType.LINE)
                            }
                            input={<OutlinedInput label="line-field" />}
                            MenuProps={MenuProps}
                        >
                          {yAxis.map((item, idx) => (
                              <MenuItem key={idx} value={item.value}>
                                {item.value}
                              </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    </>
                )}
              </>
          )}
        </Stack>
      </>
  );
}
